import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { maxPublicBatchAlYear } from "@/lib/publicBatchVisibility";
import { formatNewsMonthYear } from "@/lib/displayOrderDates";

type BranchRef = { name: string; slug: string } | null;
type BatchRef = {
  slug: string;
  custom_title: string | null;
  official_title: string | null;
  al_year: number | null;
} | null;
type AssociationRef = { name: string; slug: string } | null;

type NewsRow = {
  id: string;
  title: string;
  description: string | null;
  image_url: string | null;
  slug: string | null;
  published_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  branch: BranchRef;
  batch: BatchRef;
  association: AssociationRef;
};

function one<T>(x: T | T[] | null | undefined): T | null {
  if (x == null) return null;
  return Array.isArray(x) ? (x[0] ?? null) : x;
}

function batchLabel(b: NonNullable<BatchRef>) {
  return b.custom_title || b.official_title || "Batch";
}

function parentBadge(n: NewsRow): { label: string; href: string } | null {
  if (n.branch) return { label: n.branch.name, href: `/alumni/branches/${n.branch.slug}` };
  if (n.batch) return { label: batchLabel(n.batch), href: `/alumni/batches/${n.batch.slug}` };
  if (n.association) return { label: n.association.name, href: `/alumni/associations/${n.association.slug}` };
  return null;
}

export function OtherNewsListPage() {
  const [items, setItems] = useState<NewsRow[]>([]);

  useEffect(() => {
    void supabase
      .from("news")
      .select(
        `
        id,
        title,
        description,
        image_url,
        slug,
        published_at,
        created_at,
        updated_at,
        branch:branches(name, slug),
        batch:batches(slug, custom_title, official_title, al_year),
        association:associations(name, slug)
      `
      )
      .or("branch_id.not.is.null,batch_id.not.is.null,association_id.not.is.null")
      .order("updated_at", { ascending: false })
      .then(({ data }) => {
        const list = (data ?? []) as {
          id: string;
          title: string;
          description: string | null;
          image_url: string | null;
          slug: string | null;
          published_at: string | null;
          created_at: string | null;
          updated_at: string | null;
          branch: BranchRef | BranchRef[];
          batch: BatchRef | BatchRef[];
          association: AssociationRef | AssociationRef[];
        }[];
        const cutoff = maxPublicBatchAlYear();
        setItems(
          list
            .map((r) => ({
              id: r.id,
              title: r.title,
              description: r.description,
              image_url: r.image_url,
              slug: r.slug,
              published_at: r.published_at,
              created_at: r.created_at,
              updated_at: r.updated_at,
              branch: one(r.branch),
              batch: one(r.batch),
              association: one(r.association),
            }))
            .filter((r) => {
              const b = r.batch;
              if (!b) return true;
              if (b.al_year == null) return true;
              return b.al_year <= cutoff;
            })
        );
      });
  }, []);

  return (
    <div className="mx-auto max-w-6xl px-4 py-14">
      <p className="text-xs font-bold uppercase tracking-[0.3em] text-brand-maroon">
        News
      </p>
      <h1 className="mt-2 font-display text-4xl font-bold text-brand-blue">
        Affiliated News
      </h1>
      <p className="mt-4 text-brand-blue/85">
        Updates from batches, branches, and associations.
      </p>

      <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {items.length === 0 && (
          <p className="text-sm text-brand-blue/60 col-span-full">
            No news published yet.
          </p>
        )}

        {items.map((n) => {
          const badge = parentBadge(n);
          const dateLine = formatNewsMonthYear(n.updated_at ?? n.created_at);
          return (
            <article
              key={n.id}
              className="overflow-hidden rounded-2xl border border-brand-gray bg-white shadow-sm"
            >
              <div className="aspect-[16/9] bg-brand-gray">
                {n.image_url ? (
                  <img src={n.image_url} alt="" className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full items-center justify-center text-xs text-brand-blue/50">
                    No image
                  </div>
                )}
              </div>
              <div className="p-6">
                {badge ? (
                  <Link
                    to={badge.href}
                    className="text-xs font-bold uppercase tracking-wide text-brand-maroon hover:underline"
                  >
                    {badge.label}
                  </Link>
                ) : null}
                {dateLine ? (
                  <time
                    dateTime={n.updated_at ?? n.created_at ?? undefined}
                    className="mt-2 block text-xs font-semibold uppercase tracking-wide text-brand-maroon/80"
                  >
                    {dateLine}
                  </time>
                ) : null}
                <Link
                  to={n.slug ? `/news/other/${n.slug}` : "/news/other"}
                  className="mt-2 block font-display text-2xl font-bold text-brand-blue hover:underline"
                >
                  {n.title}
                </Link>
                {n.description ? (
                  <p className="mt-3 text-brand-blue/85 whitespace-pre-line line-clamp-4">
                    {n.description}
                  </p>
                ) : null}
                {n.slug ? (
                  <Link
                    to={`/news/other/${n.slug}`}
                    className="mt-4 inline-block text-xs font-bold uppercase tracking-wide text-brand-maroon hover:underline"
                  >
                    Read more →
                  </Link>
                ) : null}
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}

